import { supabase } from "@/integrations/supabase/client";
import type { BadgeKey } from "./certificationBadges";
import { sanitizeBadges } from "./certificationBadges";

export interface CertificationResource {
  title: string;
  version: string;
  url: string;
}

export interface ChapterPracticeOverride {
  flashcardsEnabled?: boolean;
  matchEnabled?: boolean;
  categorizeEnabled?: boolean;
  matchBatchSize?: number;
  categoryCount?: number;
  categoryLabels?: Record<string, string>;
  categorizeIncludedKeywordIds?: string[];
  categoryAssignments?: Record<string, string>;
  borrowedKeywordIds?: string[];
  /** @deprecated Per-mode keyword selection removed. All assigned keywords go through all modes. */
  flashcardKeywordIds?: string[];
  /** @deprecated Per-mode keyword selection removed. All assigned keywords go through all modes. */
  matchKeywordIds?: string[];
}

export interface KeywordPracticeConfig {
  flashcardsEnabled: boolean;
  matchEnabled: boolean;
  categorizeEnabled: boolean;
  matchBatchSize: number;
  categoryCount: number;
  categoryLabels: Record<string, string>;
  categorizeIncludedKeywordIds: string[];
  categoryAssignments: Record<string, string>;
  borrowedKeywordIds?: string[];
  /** @deprecated Per-mode keyword selection removed. All assigned keywords go through all modes. */
  flashcardKeywordIds?: string[];
  /** @deprecated Per-mode keyword selection removed. All assigned keywords go through all modes. */
  matchKeywordIds?: string[];
  chapterOverrides?: Record<string, ChapterPracticeOverride>;
}

export const DEFAULT_KEYWORD_PRACTICE_CONFIG: KeywordPracticeConfig = {
  flashcardsEnabled: true,
  matchEnabled: true,
  categorizeEnabled: true,
  matchBatchSize: 5,
  categoryCount: 2,
  categoryLabels: {},
  categorizeIncludedKeywordIds: [],
  categoryAssignments: {},
};

/**
 * Resolve the effective practice config for a specific chapter
 * by merging cert-level defaults with any chapter-specific override.
 */
export function resolveChapterPracticeConfig(
  certConfig: KeywordPracticeConfig,
  chapterId: string
): KeywordPracticeConfig {
  const override = certConfig.chapterOverrides?.[chapterId];
  if (!override) return certConfig;
  return {
    flashcardsEnabled: override.flashcardsEnabled ?? certConfig.flashcardsEnabled,
    matchEnabled: override.matchEnabled ?? certConfig.matchEnabled,
    categorizeEnabled: override.categorizeEnabled ?? certConfig.categorizeEnabled,
    matchBatchSize: override.matchBatchSize ?? certConfig.matchBatchSize,
    categoryCount: override.categoryCount ?? certConfig.categoryCount,
    categoryLabels: override.categoryLabels ?? certConfig.categoryLabels,
    categorizeIncludedKeywordIds: override.categorizeIncludedKeywordIds ?? certConfig.categorizeIncludedKeywordIds,
    categoryAssignments: override.categoryAssignments ?? certConfig.categoryAssignments,
    borrowedKeywordIds: override.borrowedKeywordIds ?? [],
    flashcardKeywordIds: override.flashcardKeywordIds,
    matchKeywordIds: override.matchKeywordIds,
  };
}

export interface AdminCertification {
  id: number;
  certificationId: string;
  name: string;
  fullName?: string;
  code: string;
  version: string;
  level: "Foundation" | "Advanced" | "Expert" | "N/A";
  stream?: string;
  questions: number;
  totalPossiblePoints: number;
  questionsPerExam: number;
  passingScorePoints: number;
  passPercentage: number;
  timeMinutes: number;
  extensionMinutes?: number;
  status: "active" | "draft";
  requiredTier: "free" | "monthly" | "quarterly" | "yearly";
  description?: string;
  resources?: CertificationResource[];
  order?: number;
  keywordPracticeConfig?: KeywordPracticeConfig;
  showOnLanding?: boolean;
  examDistributionRules?: ExamDistributionRules | null;
  badges?: BadgeKey[];
  /** ISO date (YYYY-MM-DD) — when set, the landing page shows a red "Available until <date>" badge. Admin removes it manually. */
  availableUntil?: string | null;
}

export interface ExamDistributionLoGroup {
  id: string;
  label: string;
  loIds: string[];
  questionCount: number;
  kLevel: string;
}

export interface ExamDistributionRules {
  loGroups: ExamDistributionLoGroup[];
}

type Listener = () => void;
let _certs: AdminCertification[] = [];
let _loaded = false;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export function rowToCert(r: any): AdminCertification {
  return {
    id: r.sort_order ?? 0,
    certificationId: r.certification_id,
    name: r.name,
    fullName: r.full_name ?? undefined,
    code: r.code,
    version: r.version,
    level: r.level as AdminCertification["level"],
    stream: r.stream ?? undefined,
    questions: r.questions_count,
    totalPossiblePoints: r.total_possible_points,
    questionsPerExam: r.questions_per_exam,
    passingScorePoints: r.passing_score_points,
    passPercentage: r.pass_percentage,
    timeMinutes: r.time_minutes,
    extensionMinutes: r.extension_minutes ?? undefined,
    status: r.status as "active" | "draft",
    requiredTier: (r.required_tier ?? "free") as "free" | "monthly" | "quarterly" | "yearly",
    description: r.description ?? undefined,
    resources: r.resources ?? [],
    order: r.sort_order ?? 0,
    keywordPracticeConfig: r.keyword_practice_config
      ? { ...DEFAULT_KEYWORD_PRACTICE_CONFIG, ...(r.keyword_practice_config as any) }
      : DEFAULT_KEYWORD_PRACTICE_CONFIG,
    showOnLanding: r.show_on_landing ?? false,
    examDistributionRules: r.exam_distribution_rules ?? null,
    badges: sanitizeBadges(r.badges),
    availableUntil: (r as any).available_until ?? null,
  };
}

/**
 * Explicit column list — `exam_distribution_rules` is admin-only and not
 * SELECT-granted to the `authenticated`/`anon` roles, so `select("*")` would
 * be rejected outright. Admin tooling reads that column through the
 * `admin-cert-exam-rules` edge function instead.
 */
const CERT_COLUMNS = [
  "certification_id",
  "name",
  "full_name",
  "code",
  "version",
  "level",
  "stream",
  "status",
  "description",
  "badges",
  "available_until",
  "show_on_landing",
  "sort_order",
  "required_tier",
  "questions_count",
  "total_possible_points",
  "questions_per_exam",
  "passing_score_points",
  "pass_percentage",
  "time_minutes",
  "extension_minutes",
  "resources",
  "keyword_practice_config",
].join(", ");

async function loadFromDb() {
  const { data } = await supabase.from("certifications").select(CERT_COLUMNS).order("sort_order");
  _certs = (data ?? []).map(rowToCert);
  _loaded = true;
  notify();
}

// Initial load
loadFromDb();

export const certificationStore = {
  get: () => _certs,
  subscribe: (fn: Listener) => { listeners.add(fn); return () => listeners.delete(fn); },
  isLoaded: () => _loaded,
  reload: loadFromDb,

  async add(cert: AdminCertification) {
    const order = cert.order ?? (_certs.length > 0 ? Math.max(..._certs.map((c) => c.order ?? 0)) + 1 : 1);
    const { error } = await supabase.from("certifications").insert({
      certification_id: cert.certificationId,
      name: cert.name,
      full_name: cert.fullName ?? null,
      code: cert.code,
      version: cert.version,
      level: cert.level,
      stream: cert.stream ?? null,
      questions_count: cert.questions,
      total_possible_points: cert.totalPossiblePoints,
      questions_per_exam: cert.questionsPerExam,
      passing_score_points: cert.passingScorePoints,
      pass_percentage: cert.passPercentage,
      time_minutes: cert.timeMinutes,
      extension_minutes: cert.extensionMinutes ?? null,
      status: cert.status,
      required_tier: cert.requiredTier ?? "free",
      description: cert.description ?? null,
      resources: (cert.resources ?? []) as unknown as null,
      sort_order: order,
      show_on_landing: cert.showOnLanding ?? false,
      available_until: cert.availableUntil ?? null,
    } as any);
    if (!error) {
      cert.order = order;
      _certs = [..._certs, cert];
      notify();
    }
  },

  async update(certificationId: string, patch: Partial<AdminCertification>) {
    const dbPatch: Record<string, unknown> = {};
    if (patch.name !== undefined) dbPatch.name = patch.name;
    if (patch.fullName !== undefined) dbPatch.full_name = patch.fullName;
    if (patch.code !== undefined) dbPatch.code = patch.code;
    if (patch.version !== undefined) dbPatch.version = patch.version;
    if (patch.level !== undefined) dbPatch.level = patch.level;
    if (patch.stream !== undefined) dbPatch.stream = patch.stream;
    if (patch.questions !== undefined) dbPatch.questions_count = patch.questions;
    if (patch.totalPossiblePoints !== undefined) dbPatch.total_possible_points = patch.totalPossiblePoints;
    if (patch.questionsPerExam !== undefined) dbPatch.questions_per_exam = patch.questionsPerExam;
    if (patch.passingScorePoints !== undefined) dbPatch.passing_score_points = patch.passingScorePoints;
    if (patch.passPercentage !== undefined) dbPatch.pass_percentage = patch.passPercentage;
    if (patch.timeMinutes !== undefined) dbPatch.time_minutes = patch.timeMinutes;
    if (patch.extensionMinutes !== undefined) dbPatch.extension_minutes = patch.extensionMinutes;
    if (patch.status !== undefined) dbPatch.status = patch.status;
    if (patch.requiredTier !== undefined) dbPatch.required_tier = patch.requiredTier;
    if (patch.description !== undefined) dbPatch.description = patch.description;
    if (patch.resources !== undefined) dbPatch.resources = patch.resources;
    if (patch.order !== undefined) dbPatch.sort_order = patch.order;
    if (patch.keywordPracticeConfig !== undefined) dbPatch.keyword_practice_config = patch.keywordPracticeConfig;
    if (patch.showOnLanding !== undefined) dbPatch.show_on_landing = patch.showOnLanding;
    if (patch.examDistributionRules !== undefined) dbPatch.exam_distribution_rules = patch.examDistributionRules;
    if (patch.badges !== undefined) dbPatch.badges = sanitizeBadges(patch.badges);
    if (patch.availableUntil !== undefined) dbPatch.available_until = patch.availableUntil;

    const { error } = await supabase.from("certifications").update(dbPatch).eq("certification_id", certificationId);
    if (!error) {
      _certs = _certs.map((c) => (c.certificationId === certificationId ? { ...c, ...patch } : c));
      notify();
    }
  },

  async remove(certificationId: string) {
    const { error } = await supabase.from("certifications").delete().eq("certification_id", certificationId);
    if (!error) {
      _certs = _certs.filter((c) => c.certificationId !== certificationId);
      notify();
    }
  },

  async reorderByIds(orderedIds: string[]) {
    if (_certs.length !== orderedIds.length) return;
    const sorted = [..._certs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const orders = sorted.map((c, i) => c.order ?? i + 1);
    const idToOrder = new Map(orderedIds.map((id, i) => [id, orders[i]]));
    _certs = _certs.map((c) => {
      const newOrder = idToOrder.get(c.certificationId);
      return newOrder !== undefined ? { ...c, order: newOrder } : c;
    });
    notify();
    // Persist all order changes
    for (const [certId, order] of idToOrder) {
      await supabase.from("certifications").update({ sort_order: order }).eq("certification_id", certId);
    }
  },
};
